/**
 * GHL SSO iframe helpers.
 *
 * SECURE MECHANISM (GHL Marketplace / private app iframe SSO)
 * ----------------------------------------------------------
 * GHL does NOT pass a bearer token. When ClearIntake is loaded inside the GHL
 * iframe, the parent window holds an ENCRYPTED user-data payload (encrypted with
 * the app's Shared Secret / "SSO Key"). The iframe obtains it via postMessage:
 *
 *   1. window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*')
 *   2. GHL replies { message: 'REQUEST_USER_DATA_RESPONSE', payload: <encrypted> }
 *
 * That encrypted payload is POSTed to /api/auth/ghl-sso, where the backend
 * decrypts + validates it and derives the location/user (lib/ghl-sso-verify.ts).
 * See requestGHLUserData below; the exchange itself lives in auth-context's
 * loginWithSSO.
 *
 * The legacy ?ssoToken= URL parameter is retained ONLY as a fallback in case a
 * given GHL surface still provides it.
 *
 * The staff JWT is held in memory only (see auth-context.tsx / lib/api.ts) —
 * never persisted to sessionStorage or localStorage, per the HIPAA policy.
 */

// ---------------------------------------------------------------------------
// Token helpers
// ---------------------------------------------------------------------------

/**
 * Extract the ssoToken from the current URL's query string.
 * Returns null if the parameter is absent (e.g., patient portal, public form).
 *
 * NOTE: this is the LEGACY fallback. The primary, secure path is the encrypted
 * postMessage payload from requestGHLUserData().
 */
export function extractSSOToken(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('ssoToken');
}

/**
 * Returns true if we appear to be running inside a GHL iframe.
 * Checks window.parent and the ssoToken param as heuristics.
 */
export function isGHLIframe(): boolean {
  if (window.parent !== window) return true;   // inside an iframe
  const params = new URLSearchParams(window.location.search);
  return params.get('ssoToken') !== null || params.get('locationId') !== null;
}

// ---------------------------------------------------------------------------
// Encrypted user-data request (secure SSO)
// ---------------------------------------------------------------------------

/** Message name GHL listens for to release the encrypted user-data payload. */
const REQUEST_MESSAGE = 'REQUEST_USER_DATA';
/** Message name GHL replies with, carrying the encrypted payload. */
const RESPONSE_MESSAGE = 'REQUEST_USER_DATA_RESPONSE';

/**
 * Ask the GHL parent window for the encrypted user-data payload via postMessage.
 *
 * Resolves with the encrypted payload string (an opaque blob the backend
 * decrypts), or rejects if no response arrives before `timeoutMs`. The message
 * listener is always cleaned up — on success, timeout, and rejection.
 *
 * The encrypted payload is NOT plaintext PHI and is never logged here; it is
 * forwarded as-is to /api/auth/ghl-sso.
 *
 * @param timeoutMs how long to wait for GHL to respond (default 5000ms)
 */
export function requestGHLUserData(timeoutMs = 5000): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    // Not in an iframe -> no parent to ask. Fail fast so callers can fall back.
    if (window.parent === window) {
      reject(new Error('Not running inside a GHL iframe'));
      return;
    }

    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = (): void => {
      window.removeEventListener('message', onMessage);
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const onMessage = (event: MessageEvent): void => {
      const data = event.data as { message?: unknown; payload?: unknown } | null | undefined;
      if (!data || typeof data !== 'object') return;
      if (data.message !== RESPONSE_MESSAGE) return;

      // GHL emits the encrypted blob as the `payload` field; accept a non-empty string.
      const payload = typeof data.payload === 'string' ? data.payload : null;

      if (settled) return;
      settled = true;
      cleanup();

      if (payload && payload.length > 0) {
        resolve(payload);
      } else {
        reject(new Error('GHL returned an empty SSO payload'));
      }
    };

    window.addEventListener('message', onMessage);

    timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('Timed out waiting for GHL user data'));
    }, timeoutMs);

    // Ask the parent (GHL) to release the encrypted payload. '*' target origin is
    // required because GHL serves the parent from multiple region hosts; the
    // payload itself is encrypted and validated server-side, so the origin here
    // does not weaken trust.
    window.parent.postMessage({ message: REQUEST_MESSAGE }, '*');
  });
}